import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaUpload,
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaShieldAlt,
  FaLightbulb,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpBrandProtectionKeywords.css";

const SpBrandProtectionKeywords = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Estados para configuracion
  const [siteType, setSiteType] = useState("AMAZON_AND_BEYOND");

  // Estados para archivos
  const [productsFile, setProductsFile] = useState(null);

  // Estados para keywords de marca
  const [brandName, setBrandName] = useState("");
  const [brandKeywords, setBrandKeywords] = useState([
    { keyword: "", matchType: "EXACT" },
  ]);

  // Estados de configuracion de campaign
  const [defaultBid, setDefaultBid] = useState(1.0); // Higher bid for brand protection
  const [defaultBudget, setDefaultBudget] = useState(15);
  const [campaignState, setCampaignState] = useState("PAUSED");
  const [biddingStrategy, setBiddingStrategy] = useState("SALES_UP_AND_DOWN"); // More aggressive for brand

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [generatingVariations, setGeneratingVariations] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creationComplete, setCreationComplete] = useState(false);

  // Bulk actions states
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkBid, setBulkBid] = useState("");

  // Manejar carga de archivo de productos
  const handleProductsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setProductsFile(file);
      setError(null);
    } else {
      setError("Please select a valid CSV file");
    }
  };

  // Manejar cambio en keywords de marca
  const handleKeywordChange = (index, field, value) => {
    const updatedKeywords = [...brandKeywords];
    updatedKeywords[index][field] = value;
    setBrandKeywords(updatedKeywords);
  };

  // Agregar nuevo keyword
  const addKeyword = () => {
    setBrandKeywords([...brandKeywords, { keyword: "", matchType: "EXACT" }]);
  };

  // Delete keyword
  const removeKeyword = (index) => {
    if (brandKeywords.length > 1) {
      const updatedKeywords = brandKeywords.filter((_, i) => i !== index);
      setBrandKeywords(updatedKeywords);
    }
  };

  // Generate brand variations automatically
  const generateBrandVariations = () => {
    if (!brandName.trim()) {
      setError("Enter your brand name first");
      return;
    }

    setGeneratingVariations(true);

    const name = brandName.trim();
    const nameLower = name.toLowerCase();
    const nameNoSpaces = name.replace(/\s+/g, "");

    // Generate common variations
    const variations = [
      // Original and lowercase ? { keyword : name, matchType: "EXACT" },
      { keyword: nameLower, matchType: "EXACT" },
      // Without spaces ? { keyword : nameNoSpaces, matchType: "EXACT" },
      { keyword: nameNoSpaces.toLowerCase(), matchType: "EXACT" },
      // With common suffixes ? { keyword : `${name} products`, matchType: "PHRASE" },
      { keyword: `${name} official`, matchType: "PHRASE" },
      { keyword: `${name} store`, matchType: "PHRASE" },
      { keyword: `${name} brand`, matchType: "PHRASE" },
      // Broad match for general protection ? { keyword : name, matchType: "BROAD" },
    ];

    // Common misspellings (simple substitutions)
    const misspellings = generateMisspellings(name);
    misspellings.forEach((ms) => {
      variations.push({ keyword: ms, matchType: "EXACT" });
    });

    // Remove duplicates
    const uniqueVariations = variations.filter(
      (v, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t.keyword.toLowerCase() === v.keyword.toLowerCase() &&
            t.matchType === v.matchType,
        ),
    );

    setBrandKeywords(uniqueVariations);
    setGeneratingVariations(false);
    setResult({
      success: true,
      message: `${uniqueVariations.length} variaciones de marca generadas`,
    });
  };

  // Generate simple misspellings
  const generateMisspellings = (word) => {
    const misspellings = [];
    const chars = word.split("");
    const swaps = { i: "y", y: "i", c: "k", k: "c", s: "z", z: "s" };

    // Double letters
    for (let i = 0; i < chars.length && misspellings.length < 3; i++) {
      if (/[aeiou]/i.test(chars[i])) {
        const doubled = [...chars];
        doubled.splice(i, 0, chars[i]);
        misspellings.push(doubled.join(""));
      }
    }

    for (let i = 0; i < chars.length && misspellings.length < 5; i++) {
      const lower = chars[i].toLowerCase();
      if (swaps[lower]) {
        const swapped = [...chars];
        swapped[i] =
          chars[i] === chars[i].toUpperCase() ? swaps[lower].toUpperCase() : swaps[lower];
        misspellings.push(swapped.join(""));
      }
    }

    return misspellings.slice(0, 5);
  };

  // Generate brand protection campaigns
  const handleGenerateCampaigns = async () => {
    if (!productsFile) {
      setError("You must upload the products file (ASIN, SKU)");
      return;
    }

    const validKeywords = brandKeywords.filter((k) => k.keyword.trim());
    if (validKeywords.length === 0) {
      setError("You must ingresar al menos una keyword de marca");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("products_file", productsFile);
      formData.append("site_type", siteType);
      formData.append("default_bid", defaultBid);
      formData.append("default_budget", defaultBudget);
      formData.append("bidding_strategy", biddingStrategy);
      formData.append("brand_name", brandName);
      formData.append("brand_keywords", JSON.stringify(validKeywords));

      const response = await axios.post(
        `${API_URL}/sp-brand-protection/generate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        // Agregar campo 'selected' a cada campaign
        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true,
        }));

        setCampaigns(campaignsWithSelection);

        setResult({
          success: true,
          message: `${generatedCampaigns.length} campaigns de proteccion de marca generadas.`,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error generating campaigns");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error || err.message || "Error generating campaigns",
      );
    } finally {
      setLoading(false);
    }
  };

  // Create campaigns en Amazon
  const handleCreateInAmazon = async () => {
    const selectedCampaigns = campaigns.filter((c) => c.selected);

    if (selectedCampaigns.length === 0) {
      setError("You must select at least one campaign to create in Amazon");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError(
          "Please select an account in the selector above before creating campaigns",
        );
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/sp-brand-protection/create-in-amazon`,
        {
          profileId,
          campaigns: selectedCampaigns,
          campaignState,
          siteType,
          biddingStrategy,
        },
      );

      if (response.data.success) {
        const amazonResults = response.data.results || [];
        const errors = response.data.errors || [];

        setResult({
          success: true,
          message: response.data.message,
          count: response.data.campaigns_created,
          amazonResults: amazonResults,
          errors: errors,
        });

        const campaignsForTable = amazonResults.map((result) => ({
          "Campaign Name": result.campaignName,
          "Campaign ID": result.campaignId,
          "Ad Group ID": result.adGroupId,
          Keyword: result.keyword,
          "Match Type": result.matchType,
          Status: "Created in Amazon",
        }));

        setCampaigns(campaignsForTable);
        setCreationComplete(true);
      } else {
        setError(response.data.error || "Error creating campaigns en Amazon");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error ||
          err.message ||
          "Error creating campaigns en Amazon",
      );
    } finally {
      setLoading(false);
    }
  };

  // Toggle seleccion
  const toggleCampaignSelection = (index) => {
    const updatedCampaigns = [...campaigns];
    updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
    setCampaigns(updatedCampaigns);
  };

  const selectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: true }));
    setCampaigns(updatedCampaigns);
  };

  const deselectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: false }));
    setCampaigns(updatedCampaigns);
  };

  // Apply accion masiva
  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Select a bulk action");
      return;
    }

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Select al menos una campaign");
      return;
    }

    const updatedCampaigns = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;

      const updated = { ...campaign };

      switch (bulkAction) {
        case "budget":
          if (bulkBudget && parseFloat(bulkBudget) > 0) {
            updated.Budget = parseFloat(bulkBudget);
          }
          break;
        case "bid":
          if (bulkBid && parseFloat(bulkBid) > 0) {
            updated.Bid = parseFloat(bulkBid);
          }
          break;
        default:
          break;
      }

      return updated;
    });

    setCampaigns(updatedCampaigns);
    setBulkAction("");
    setBulkBudget("");
    setBulkBid("");
    setError(null);
    setResult({
      success: true,
      message: `Accion aplicada a ${selectedCount} campaign(s)`,
      count: selectedCount,
    });
  };

  // Descargar CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;

    const headers = Object.keys(campaigns[0]).filter((h) => h !== "selected");
    const csvContent = [
      headers.join(","),
      ...campaigns.map((campaign) =>
        headers
          .map((header) => {
            const value = campaign[header];
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `sp_brand_protection_campaigns_${Date.now()}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset
  const handleReset = () => {
    setProductsFile(null);
    setBrandName("");
    setBrandKeywords([{ keyword: "", matchType: "EXACT" }]);
    setCampaigns([]);
    setResult(null);
    setError(null);
    setCreationComplete(false);
  };

  // Get match type badge color
  const getMatchTypeBadge = (matchType) => {
    switch (matchType) {
      case "EXACT":
        return "success";
      case "PHRASE":
        return "warning";
      case "BROAD":
        return "info";
      default:
        return "secondary";
    }
  };

  return (
    <Layout>
      <Container fluid className="sp-brand-protection-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="text-white mb-3">
              <FaShieldAlt className="me-2" />
              Brand Protection Keywords - Sponsored Products
            </h2>
            <p className="text-muted">
              Protege tu marca con campaigns SP que targetean busquedas de tu
              marca para evitar que competidores roben trafico
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {result && result.success && !creationComplete && (
          <Alert variant="success" onClose={() => setResult(null)} dismissible>
            <FaCheckCircle className="me-2" />
            {result.message}
          </Alert>
        )}

        {/* Configuration Section */}
        {!result.amazonResults && (
          <>
            {/* Info Banner */}
            <Alert variant="warning" className="mb-4">
              <FaShieldAlt className="me-2" />
              <strong>Proteccion de Marca:</strong> Manual Targeting • Keyword
              Targeting • Keywords de tu marca para proteger trafico
            </Alert>

            {/* SITE Selection */}
            <div className="config-section-card mb-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  1
                </Badge>
                Select SITE
              </h5>
              <Form.Select
                value={siteType}
                onChange={(e) => setSiteType(e.target.value)}
                className="site-selector"
              >
                <option value="AMAZON_AND_BEYOND">
                  Amazon and Beyond (Multi-Marketplace)
                </option>
                <option value="AMAZON_BUSINESS">
                  Amazon Business (Single Marketplace)
                </option>
              </Form.Select>
            </div>

            {/* Upload Section */}
            <div className="upload-grid">
              <div className={`upload-card-new ${productsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Required</div>
                <Badge bg="primary" className="step-badge">
                  2
                </Badge>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Productos CSV</div>
                <div className="upload-card-desc">
                  Columnas requeridas: ASIN, SKU
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {productsFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleProductsFileChange}
                    hidden
                  />
                </label>
                {productsFile && (
                  <small className="text-success mt-2">
                    {productsFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Brand Name Section */}
            <div className="config-section-card mt-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  3
                </Badge>
                Nombre de Marca
              </h5>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="text-muted">
                      Enter your brand name
                    </Form.Label>
                    <Form.Control
                      type="text" placeholder="Ej : Nike, Apple, Samsung..."
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      className="brand-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button
                    variant="outline-warning"
                    onClick={generateBrandVariations}
                    disabled={generatingVariations || !brandName.trim()}
                    className="w-100"
                  >
                    {generatingVariations ? (
                      <>
                        <Spinner
                          size="sm"
                          animation="border"
                          className="me-1"
                        />
                        Generando...
                      </>
                    ) : (
                      <>
                        <FaLightbulb className="me-1" />
                        Generate Automatic Variations
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Brand Keywords Section */}
            <div className="config-section-card mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <Badge bg="primary" className="me-2">
                    4
                  </Badge>
                  Keywords de Proteccion de Marca
                </h5>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={addKeyword}
                >
                  <FaPlus className="me-1" /> Agregar Keyword
                </Button>
              </div>

              <p className="text-muted small mb-3">
                Incluye: nombre de marca, variaciones, errores ortograficos
                comunes, y frases relacionadas
              </p>

              {/* Keyword Inputs */}
              {brandKeywords.map((kw, index) => (
                <Row key={index} className="mb-3 keyword-row">
                  <Col md={7}>
                    <Form.Group>
                      <Form.Label className="text-muted small">
                        Keyword
                      </Form.Label>
                      <Form.Control
                        type="text" placeholder="Ej : tu marca, tu-marca, tumarca..."
                        value={kw.keyword}
                        onChange={(e) =>
                          handleKeywordChange(index, "keyword", e.target.value)
                        }
                        className="keyword-input"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="text-muted small">
                        Match Type
                      </Form.Label>
                      <Form.Select
                        value={kw.matchType}
                        onChange={(e) =>
                          handleKeywordChange(
                            index,
                            "matchType",
                            e.target.value,
                          )
                        }
                        className="match-type-select"
                      >
                        <option value="EXACT">EXACT</option>
                        <option value="PHRASE">PHRASE</option>
                        <option value="BROAD">BROAD</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    {brandKeywords.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeKeyword(index)}
                        className="w-100"
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}
            </div>

            {/* Campaign Configuration */}
            <div className="config-section-card mt-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  5
                </Badge>
                Configuration de Campaign
              </h5>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bid Default ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.02"
                      value={defaultBid}
                      onChange={(e) =>
                        setDefaultBid(parseFloat(e.target.value))
                      }
                    />
                    <Form.Text className="text-warning">
                      Recomendado: Bid alto para ganar posicion en tu marca
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Budget Diario ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      value={defaultBudget}
                      onChange={(e) =>
                        setDefaultBudget(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bidding Strategy
                    </Form.Label>
                    <Form.Select
                      value={biddingStrategy}
                      onChange={(e) => setBiddingStrategy(e.target.value)}
                    >
                      <option value="SALES_UP_AND_DOWN">
                        Dynamic Bids - Up and Down (Recomendado)
                      </option>
                      <option value="SALES_DOWN_ONLY">
                        Dynamic Bids - Down Only
                      </option>
                      <option value="FIXED_BID">Fixed Bids</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Type
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="Manual Targeting"
                      disabled
                      className="bg-dark text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Strategy
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="Keyword Targeting"
                      disabled
                      className="bg-dark text-warning fw-bold"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Generate Button */}
            <div className="generate-section mt-4">
              <button className={`btn-generate-campaigns ${!loading && productsFile && brandKeywords.some((k) => k.keyword.trim()) ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={
                  loading ||
                  !productsFile ||
                  !brandKeywords.some((k) => k.keyword.trim())
                }
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generando campaigns de proteccion...
                  </>
                ) : (
                  <>
                    <FaShieldAlt />
                    Generate Brand Protection Campaigns
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Results Section */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="bg-dark text-white p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaShieldAlt className="me-2" />
                      Campaigns de Proteccion de Marca ({campaigns.length})
                    </h5>
                  </div>

                  <div>
                    {/* Bulk Actions Row */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-white fw-bold">
                          Bulk actions:
                        </span>
                        <Form.Select
                          size="sm"
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Select accion</option>
                          <option value="budget">Asignar presupuesto...</option>
                          <option value="bid">Asignar bid...</option>
                        </Form.Select>

                        {bulkAction === "budget" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="Nuevo presupuesto"
                            value={bulkBudget}
                            onChange={(e) => setBulkBudget(e.target.value)}
                            style={{ width: "150px" }}
                            min="0"
                            step="0.01"
                          />
                        )}

                        {bulkAction === "bid" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="Nuevo bid"
                            value={bulkBid}
                            onChange={(e) => setBulkBid(e.target.value)}
                            style={{ width: "150px" }}
                            min="0.02"
                            step="0.01"
                          />
                        )}

                        {bulkAction && (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={applyBulkAction}
                          >
                            Apply
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={campaignState}
                          onChange={(e) => setCampaignState(e.target.value)}
                          style={{ width: "auto" }}
                          disabled={creationComplete}
                        >
                          <option value="ENABLED">Create como: Enabled</option>
                          <option value="PAUSED">Create como: Paused</option>
                        </Form.Select>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleDownloadCampaigns}
                        >
                          <FaDownload className="me-1" /> Descargar CSV
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleReset}
                        >
                          <FaRocket className="me-1" /> Nueva Generacion
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive bg-dark" style={{ maxHeight : "600px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    variant="dark"
                    className="align-middle mb-0"
                    size="sm" style={{ fontSize : "0.85rem" }}
                  >
                    <thead className="text-warning sticky-top bg-dark">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th width="50">#</th>
                        <th width="300">Campaign Name</th>
                        <th width="100">ASIN</th>
                        <th width="150">Keyword</th>
                        <th width="100">Match Type</th>
                        <th width="80">Bid</th>
                        <th width="80">Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={campaign.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className="text-truncate" style={{ maxWidth : "280px" }}
                            >
                              {campaign["Campaign Name"]}
                            </div>
                          </td>
                          <td className="text-info">{campaign.ASIN}</td>
                          <td className="text-warning">{campaign.Keyword}</td>
                          <td>
                            <Badge
                              bg={getMatchTypeBadge(campaign["Match Type"])}
                            >
                              {campaign["Match Type"]}
                            </Badge>
                          </td>
                          <td>${campaign.Bid}</td>
                          <td>${campaign.Budget}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="fw-bold"
                    >
                      ← Cancel
                    </Button>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="text-muted small">
                      Total: {campaigns.length} | Selected:{" "}
                      {campaigns.filter((c) => c.selected).length}
                    </div>
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                      style={{
                        fontWeight: "bold",
                        padding: "6px 20px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Creando en Amazon...
                        </>
                      ) : creationComplete ? (
                        <>
                          <FaCheckCircle className="me-1" /> Campaigns Creadas
                        </>
                      ) : (
                        <>
                          <FaShieldAlt className="me-1" /> Create Campaigns of
                          Proteccion
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements Section */}
        {!result && (
          <div className="requirements-section mt-4">
            <span className="requirements-title">
              🛡️ Especificaciones Brand Protection Keywords
            </span>
            <div className="req-item">
              <strong>Productos CSV:</strong> Debe contener 'ASIN', 'SKU'.
            </div>
            <div className="req-item">
              <strong>Keywords:</strong> Nombre de marca, variaciones, errores
              ortograficos.
            </div>
            <div className="req-item">
              <strong>Match Types:</strong> EXACT (recomendado), PHRASE, BROAD.
            </div>
            <div className="req-item">
              <strong>Targeting:</strong> Manual Targeting con Keyword
              Targeting.
            </div>
            <div className="req-item">
              <strong>Estrategia:</strong> Bids altos para ganar posicion en
              busquedas de tu marca.
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpBrandProtectionKeywords;

